import { create } from "zustand";

/**
 * Client-side mirror of the ATLAS Daily Reports event stream
 * (`bo.report.v1.{generated, edit_submitted, grid_locked}`, piped through the
 * pks-bff WebSocket bridge as `kind: 'report_*'`).
 */

export interface ReportGeneratedEvent {
  reportId: string;
  runId: string;
  entityId: string;
  deskId: string | null;
  businessDate: string;
  version: number;
  sealHash?: string | null;
}

export interface ReportEditEvent {
  runId: string;
  deskId: string | null;
  userId: string;
  sectionCode: string;
  editedAt: string | null;
}

export interface ReportLockedEvent {
  deskId: string;
  businessDate: string;
  lockedAt: string | null;
}

interface ReportState {
  /** report_id → ISO timestamp of the most recent `report_generated` event. */
  generatedAt: Map<string, string>;
  /** "desk_id|business_date" set of locked grids. */
  lockedKeys: Set<string>;
  /** run_id → most recent batch-edit event (UI uses this to reconcile cache). */
  edits: Map<string, ReportEditEvent>;
  applyGenerated: (evt: ReportGeneratedEvent) => void;
  applyEdit: (evt: ReportEditEvent) => void;
  applyLocked: (evt: ReportLockedEvent) => void;
  isLocked: (deskId: string, businessDate: string) => boolean;
}

function lockKey(deskId: string, date: string): string {
  return `${deskId}|${date}`;
}

export const useReportStore = create<ReportState>((set, get) => ({
  generatedAt: new Map<string, string>(),
  lockedKeys: new Set<string>(),
  edits: new Map<string, ReportEditEvent>(),
  applyGenerated: (evt) =>
    set((s) => {
      const next = new Map(s.generatedAt);
      next.set(evt.reportId, new Date().toISOString());
      return { generatedAt: next };
    }),
  applyEdit: (evt) =>
    set((s) => {
      const next = new Map(s.edits);
      next.set(evt.runId, evt);
      return { edits: next };
    }),
  applyLocked: (evt) =>
    set((s) => {
      const next = new Set(s.lockedKeys);
      next.add(lockKey(evt.deskId, evt.businessDate));
      return { lockedKeys: next };
    }),
  isLocked: (deskId, businessDate) => get().lockedKeys.has(lockKey(deskId, businessDate)),
}));
